import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail, track } from "@/lib/api";
import { PathwayCard } from "@/components/PathwayCard";
import { Verdict, SavingsBox } from "@/components/Verdict";
import { HOME_EXAMPLES } from "@/lib/pathways";
import { EmailCapture } from "@/components/EmailCapture";
import { CheaperWayCTA } from "@/components/CheaperWayCTA";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ChevronDown } from "lucide-react";

export default function Find() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") || "");
  const [price, setPrice] = useState("");
  const [billing, setBilling] = useState("one-time");
  const [showCtx, setShowCtx] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [limitHit, setLimitHit] = useState(false);
  const [usage, setUsage] = useState(null);
  const { user } = useAuth();
  const ranFor = useRef(null);

  useEffect(() => { api.get("/find-usage").then((r) => setUsage(r.data)).catch(() => {}); }, [user]);

  const run = async (q) => {
    const term = (q ?? query).trim();
    if (!term) return;
    setLoading(true);
    setResult(null);
    setLimitHit(false);
    try {
      const body = { query: term };
      if (price) { body.current_price = parseFloat(price); body.billing = billing; }
      const { data } = await api.post("/find", body);
      setResult(data.result);
      setUsage(data.find_usage || null);
      track("recommendation_viewed", { query: term });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      if (err.response?.status === 402) { setLimitHit(true); track("free_audit_limit_reached"); }
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const q = params.get("q");
    if (q && ranFor.current !== q) {
      ranFor.current = q;
      setQuery(q);
      run(q);
    }
    // eslint-disable-next-line
  }, [params]);

  const savePathway = async (pw) => {
    if (!user) { toast.error("Log in to save this to your Notes."); return; }
    try {
      await api.post("/saved", {
        title: `${pw.type}: ${pw.title}`, kind: "alternative", note: pw.description,
        target_price: pw.estimated_cost, payload: pw,
      });
      toast.success("Saved to your Notes.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submit = (e) => {
    e.preventDefault();
    setParams({});
    ranFor.current = null;
    run();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="eyebrow mb-4">FIND THE CHEAPER WAY</div>
      <h1 className="font-display text-4xl md:text-6xl font-bold leading-[0.95] mb-8">
        WHAT ARE YOU ABOUT<br />TO SPEND MONEY ON?
      </h1>
      {usage && <div data-testid="find-allowance" className="font-label text-[10px] tracking-widest text-[#D4AF37] mb-5">
        {usage.unlimited ? "UNLIMITED FINDS" : `${usage.remaining} OF ${usage.limit} FREE FINDS REMAINING TODAY`}
      </div>}

      <form onSubmit={submit} className="border hairline bg-[#141417]">
        <div className="flex flex-col md:flex-row">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`"I need ${HOME_EXAMPLES[0]}"`}
            data-testid="find-search-input"
            className="flex-1 bg-transparent px-6 py-6 text-xl md:text-2xl font-display text-stone-100 placeholder:text-stone-600 outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="find-submit-btn"
            className="font-label text-xs tracking-widest bg-[#E63946] text-white px-8 py-6 hover:bg-[#c62d39] transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {loading ? "AUDITING…" : "FIND THE CHEAPER WAY →"}
          </button>
        </div>
        <button type="button" onClick={() => setShowCtx(!showCtx)} data-testid="toggle-context-btn"
          className="w-full border-t hairline px-6 py-3 flex items-center gap-2 font-label text-[10px] tracking-widest text-stone-500 hover:text-stone-300">
          <ChevronDown className={`w-3 h-3 transition-transform ${showCtx ? "rotate-180" : ""}`} /> ADD WHAT YOU CURRENTLY PAY (OPTIONAL)
        </button>
        {showCtx && (
          <div className="border-t hairline px-6 py-4 flex flex-col sm:flex-row gap-3">
            <input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Current price ($)" type="number"
              data-testid="find-price-input"
              className="bg-transparent border hairline px-4 py-3 text-sm font-label outline-none focus:border-[#E63946]" />
            <select value={billing} onChange={(e) => setBilling(e.target.value)} data-testid="find-billing-select"
              className="bg-[#141417] border hairline px-4 py-3 text-sm font-label outline-none focus:border-[#E63946]">
              <option value="one-time">one-time</option>
              <option value="monthly">monthly</option>
            </select>
          </div>
        )}
      </form>

      {!result && !loading && (
        <div className="mt-8">
          <div className="font-label text-[10px] tracking-widest text-stone-500 mb-3">TRY:</div>
          <div className="flex flex-wrap gap-2">
            {HOME_EXAMPLES.map((ex) => (
              <button key={ex} onClick={() => { setQuery(`I need ${ex}`); setParams({}); ranFor.current = null; run(`I need ${ex}`); }}
                data-testid={`example-${ex.replace(/\s+/g, "-")}`}
                className="font-label text-xs border hairline px-3 py-2 text-stone-400 hover:border-[#E63946] hover:text-[#E63946] transition-colors">
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-20 flex flex-col items-center text-center">
          <Loader2 className="w-8 h-8 text-[#E63946] animate-spin mb-4" />
          <p className="font-display text-2xl text-stone-300">Running it through the audit…</p>
        </div>
      )}

      {limitHit && (
        <div data-testid="audit-limit-box" className="mt-12 border-2 border-[#EF3340] bg-[#141217] p-8">
          <div className="eyebrow mb-3">YOU'VE USED TODAY'S 3 FREE FINDS.</div>
          <p className="font-serif text-xl text-stone-200 max-w-2xl mb-2">Want unlimited Finds? Choose $6.99/month, $49.99/year, or $99.99 lifetime—or come back when your free allowance resets tomorrow.</p>
          <Link to="/pro" data-testid="limit-go-pro" className="mt-5 inline-block font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-4">GO UNLIMITED →</Link>
        </div>
      )}

      {result && (
        <div className="mt-12 space-y-10">
          <div>
            <div className="eyebrow mb-3">{result.category}</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold leading-[0.95]">{result.headline}</h2>
          </div>

          <Verdict verdict={result.verdict} timing={result.buy_timing} />
          <SavingsBox savings={result.savings} />

          <div>
            <div className="flex items-baseline justify-between mb-5">
              <h3 className="font-label text-sm tracking-widest text-[#EF3340]">THE WAYS · {result.pathways?.length}</h3>
              <span className="font-label text-[10px] text-stone-500">FULL PRICE ~ ${result.estimated_full_price}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(result.pathways || []).map((pw, i) => (
                <PathwayCard key={i} pathway={pw} index={i} onSave={savePathway} />
              ))}
            </div>
          </div>

          <div className="border-t hairline pt-8">
            <CheaperWayCTA compact />
          </div>

          <div className="border-t hairline pt-8">
            <EmailCapture postAudit compact />
          </div>
        </div>
      )}
    </div>
  );
}
